import {Component, ElementRef, Input, OnChanges, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {DonneesImportFormat} from '../parametre-donnees.types';
import {ParametreDonneesStateService} from '../parametre-donnees-state.service';
import {ParametreDonneesApiService} from '../parametre-donnees-api.service';
import {DataImportMappingDto, resolveRefModuleTableDbId} from '../parametre-donnees-api.models';
import {getParametrageSessionIds} from '../parametre-donnees-session.util';
import {ProductAttributeOption, ProductsService} from '../../../../services/products/products.service';
import {catchError, finalize} from 'rxjs/operators';
import {of} from 'rxjs';

interface MappingRow {
  colonneFichier: string;
  champCible: string;
  transformation: string;
}

@Component({
  selector: 'app-parametre-donnees-import-tab',
  templateUrl: './parametre-donnees-import-tab.component.html',
  styleUrls: ['./parametre-donnees-import-tab.component.scss']
})
export class ParametreDonneesImportTabComponent implements OnChanges {
  @Input() moduleKey = 'produits';

  @ViewChild('fileIn') fileIn!: ElementRef<HTMLInputElement>;
  @ViewChild('nouveauFormatModalTpl') nouveauFormatModalTpl!: TemplateRef<unknown>;
  @ViewChild('confirmDeleteFormatModalTpl') confirmDeleteFormatModalTpl!: TemplateRef<unknown>;

  formats: DonneesImportFormat[] = [];
  selectedFormat: DonneesImportFormat | null = null;

  /** true = grille « Modèle 1 / 2 / … », false = écran détail (mapping, fichier, simulation). */
  afficherListeModeles = true;

  mapping: MappingRow[] = [{ colonneFichier: '', champCible: '', transformation: '' }];

  previewLines: string[] = [];
  simulationResult = '';
  simulating = false;
  dragOver = false;
  lastFile: File | null = null;

  mappingLoading = false;
  savingMapping = false;
  deletingRowIndex: number | null = null;

  /** Modal « Nouveau format » */
  nouveauFormatNom = '';
  nouveauFormatType = '';
  creatingFormat = false;

  /** Suppression d’un format d’import. */
  deletingFormatId: string | null = null;
  formatToDelete: DonneesImportFormat | null = null;

  /** GET /api/products/attributes — « Champ cible » (module produits uniquement), comme Export. */
  productAttributeOptions: ProductAttributeOption[] = [];

  constructor(
    private readonly state: ParametreDonneesStateService,
    private readonly api: ParametreDonneesApiService,
    private readonly toastr: ToastrService,
    private readonly modal: NgbModal,
    private readonly productsService: ProductsService
  ) {}

  /** Onglet « produits » / « products » : ng-select alimenté par /api/products/attributes. */
  get isProductsModule(): boolean {
    const k = (this.moduleKey || '').toLowerCase();
    return k === 'produits' || k === 'products';
  }

  compareImportFormats(a: DonneesImportFormat | null, b: DonneesImportFormat | null): boolean {
    if (!a || !b) {
      return !a && !b;
    }
    return String(a.id) === String(b.id);
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['moduleKey']) {
      this.loadLists();
    }
  }

  private loadLists(): void {
    this.formats = this.state.getImportFormats(this.moduleKey);
    this.afficherListeModeles = true;
    this.selectedFormat = null;
    this.simulationResult = '';
    this.lastFile = null;
    this.previewLines = [];
    this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
    this.loadProductAttributesForModule();
  }

  private loadProductAttributesForModule(): void {
    if (!this.isProductsModule) {
      this.productAttributeOptions = [];
      return;
    }
    this.productsService.getProductAttributes().subscribe({
      next: (rows) => {
        this.productAttributeOptions = rows;
      },
      error: () => {
        this.productAttributeOptions = [];
        this.toastr.warning('Impossible de charger les attributs produits (/api/products/attributes).', 'Import');
      }
    });
  }

  ouvrirModele(f: DonneesImportFormat): void {
    this.selectedFormat = f;
    this.afficherListeModeles = false;
    this.simulationResult = '';
    this.loadMappingsForSelected();
  }

  retourListeModeles(): void {
    this.afficherListeModeles = true;
  }

  onFormatChange(): void {
    this.simulationResult = '';
    this.loadMappingsForSelected();
  }

  supprimerFormatSelectionne(): void {
    const f = this.selectedFormat;
    if (!f || this.deletingFormatId != null) {
      return;
    }
    this.executerSuppressionFormat(f);
  }

  ouvrirPopupConfirmationSuppression(f: DonneesImportFormat, ev?: Event): void {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    if (!f || this.deletingFormatId != null) {
      return;
    }
    this.formatToDelete = f;
    this.modal.open(this.confirmDeleteFormatModalTpl, {
      centered: true,
      backdrop: 'static',
      size: 'md'
    });
  }

  confirmerSuppressionDepuisPopup(modal: NgbActiveModal): void {
    const f = this.formatToDelete;
    this.formatToDelete = null;
    modal.close();
    if (!f || this.deletingFormatId != null) {
      return;
    }
    this.executerSuppressionFormat(f);
  }

  private executerSuppressionFormat(f: DonneesImportFormat): void {
    if (this.deletingFormatId != null) {
      return;
    }
    const formatId = Number(f.id);
    const { stationId, userId } = getParametrageSessionIds();
    if (!Number.isFinite(formatId) || formatId <= 0 || stationId <= 0 || userId <= 0) {
      this.toastr.error('Session ou format invalide.', 'Import');
      return;
    }
    const prevSelectedId = this.selectedFormat?.id;
    const deletedId = String(f.id);
    this.deletingFormatId = deletedId;
    this.api
      .deleteDataImportFormat(formatId, stationId, userId)
      .pipe(finalize(() => (this.deletingFormatId = null)))
      .subscribe({
        next: () => {
          this.state.reloadImportFormats(this.moduleKey).subscribe(() => {
            this.formats = this.state.getImportFormats(this.moduleKey);
            if (!this.formats.length) {
              this.selectedFormat = null;
              this.afficherListeModeles = true;
              this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
            } else if (String(prevSelectedId) === deletedId) {
              this.selectedFormat = this.formats[0] ?? null;
            } else {
              this.selectedFormat =
                this.formats.find((x) => String(x.id) === String(prevSelectedId)) ?? this.formats[0] ?? null;
            }
            this.simulationResult = '';
            if (this.selectedFormat && !this.afficherListeModeles) {
              this.loadMappingsForSelected();
            } else if (!this.selectedFormat) {
              this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
            }
            this.toastr.success('Format supprimé.', 'Import');
          });
        },
        error: (err: unknown) => {
          this.toastr.error(this.httpErrorMessage(err), 'Import');
        }
      });
  }

  ajouterLigneMapping(): void {
    if (this.mappingLoading || this.savingMapping) {
      return;
    }
    this.mapping.push({ colonneFichier: '', champCible: '', transformation: '' });
  }

  supprimerLigneMapping(index: number): void {
    if (index < 0 || index >= this.mapping.length || this.mapping.length <= 1) {
      return;
    }
    this.mapping.splice(index, 1);
  }

  private loadMappingsForSelected(): void {
    if (!this.selectedFormat?.id) {
      this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
      return;
    }
    const importFormatId = Number(this.selectedFormat.id);
    const { stationId } = getParametrageSessionIds();
    const req$ =
      stationId > 0
        ? this.api.getDataImportMappingsByImportFormat(importFormatId, stationId)
        : this.api.getDataImportMappings(importFormatId);

    this.mappingLoading = true;
    req$
      .pipe(
        catchError(() => this.api.getDataImportMappings(importFormatId)),
        catchError(() => of([])),
        finalize(() => (this.mappingLoading = false))
      )
      .subscribe({
        next: (rows) => {
          if (rows.length) {
            this.mapping = rows.map((r) => ({
              colonneFichier: r.colonne_fichier,
              champCible: r.champ_cible,
              transformation: r.transformation
            }));
          } else {
            this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
          }
        },
        error: () => {
          this.mapping = [{ colonneFichier: '', champCible: '', transformation: '' }];
          this.toastr.warning('Impossible de charger le mapping import.', 'Import');
        }
      });
  }

  openNouveauFormatModal(): void {
    this.nouveauFormatNom = '';
    this.nouveauFormatType = '';
    this.modal.open(this.nouveauFormatModalTpl, {
      centered: true,
      backdrop: 'static',
      size: 'md'
    });
  }

  enregistrerNouveauFormat(modal: NgbActiveModal): void {
    const ref = this.state.getModuleRef(this.moduleKey);
    const { stationId } = getParametrageSessionIds();
    if (!ref || stationId <= 0) {
      this.toastr.error('Module ou session invalide.', 'Import');
      return;
    }

    const nom = (this.nouveauFormatNom || '').trim();
    const type = (this.nouveauFormatType || '').trim();
    if (!nom) {
      this.toastr.warning('Indiquez un nom pour le format.', 'Import');
      return;
    }
    if (!type) {
      this.toastr.warning('Indiquez un type pour le format.', 'Import');
      return;
    }
    const extension = type.startsWith('.') ? type : `.${type}`;

    this.creatingFormat = true;
    this.api
      .postDataImportFormat({
        station_id: stationId,
        table_db_id: resolveRefModuleTableDbId(ref),
        nom,
        extension,
        description: '',
        actif: true,
        config_json: {}
      })
      .pipe(finalize(() => (this.creatingFormat = false)))
      .subscribe({
        next: () => {
          modal.close();
          this.state.reloadImportFormats(this.moduleKey).subscribe(() => {
            this.formats = this.state.getImportFormats(this.moduleKey);
            this.selectedFormat = this.formats[this.formats.length - 1] ?? null;
            this.afficherListeModeles = false;
            this.simulationResult = '';
            this.loadMappingsForSelected();
            this.toastr.success('Format créé.', 'Succès');
          });
        },
        error: (err: unknown) => {
          this.toastr.error(this.httpErrorMessage(err), 'Import');
        }
      });
  }

  saveMapping(): void {
    if (!this.selectedFormat?.id) {
      this.toastr.warning('Sélectionnez un format d’import.', 'Import');
      return;
    }
    const fid = Number(this.selectedFormat.id);
    const { stationId, userId } = getParametrageSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.toastr.warning('Session invalide : station ou utilisateur introuvable.', 'Import');
      return;
    }
    for (let i = 0; i < this.mapping.length; i++) {
      const m = this.mapping[i];
      if (!m.champCible?.trim() || !m.colonneFichier?.trim()) {
        this.toastr.warning(`Ligne ${i + 1} : renseignez la colonne fichier et le champ cible.`, 'Import');
        return;
      }
    }
    const rows: DataImportMappingDto[] = this.mapping.map((m) => ({
      import_format_id: fid,
      colonne_fichier: m.colonneFichier,
      champ_cible: m.champCible,
      transformation: m.transformation
    }));

    const rowsForPut = rows.map((r) => ({
      colonne_fichier: r.colonne_fichier,
      champ_cible: r.champ_cible,
      transformation: r.transformation
    }));

    this.savingMapping = true;
    this.api
      .putDataImportFormatMappings(fid, stationId, userId, rowsForPut)
      .pipe(
        catchError(() => this.api.postDataImportMappings(fid, stationId, rows)),
        catchError(() => this.api.putDataImportMappings(fid, rows)),
        catchError(() => of(null)),
        finalize(() => (this.savingMapping = false))
      )
      .subscribe({
        next: () => {
          this.toastr.success('Mapping import enregistré.', 'Import');
          this.loadMappingsForSelected();
        },
        error: (err: unknown) => {
          this.toastr.error(this.httpErrorMessage(err), 'Import');
        }
      });
  }

  triggerFile(): void {
    this.fileIn?.nativeElement?.click();
  }

  onFile(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const f = input.files?.[0];
    if (!f) {
      return;
    }
    this.readFile(f);
    input.value = '';
  }

  onDrop(ev: DragEvent): void {
    ev.preventDefault();
    this.dragOver = false;
    const f = ev.dataTransfer?.files?.[0];
    if (f) {
      this.readFile(f);
    }
  }

  onDragOver(ev: DragEvent): void {
    ev.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(): void {
    this.dragOver = false;
  }

  private readFile(f: File): void {
    this.lastFile = f;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      this.previewLines = text.split(/\r?\n/).slice(0, 12);
      this.toastr.success(`Fichier « ${f.name} » chargé pour aperçu.`, 'Aperçu');
    };
    reader.readAsText(f.slice(0, 50000));
  }

  runSimulation(): void {
    const fmt = this.selectedFormat;
    if (!fmt?.id) {
      this.toastr.warning('Sélectionnez un format d’import.', 'Simulation');
      return;
    }
    if (!this.lastFile) {
      this.simulationResult = `Indicatif : ${this.mapping.length} colonne(s) — ${this.previewLines.length} ligne(s) en aperçu (chargez un fichier pour l’API).`;
      this.toastr.info('Chargez un fichier pour lancer la simulation côté serveur.', 'Simulation');
      return;
    }
    const fd = new FormData();
    fd.append('file', this.lastFile);
    this.simulating = true;
    this.api
      .postImportSimulate(Number(fmt.id), fd)
      .pipe(finalize(() => (this.simulating = false)))
      .subscribe({
        next: (res) => {
          this.simulationResult = typeof res === 'string' ? res : JSON.stringify(res, null, 2);
          this.toastr.success('Simulation terminée.', 'Simulation');
        },
        error: (err: unknown) => {
          this.toastr.error(this.httpErrorMessage(err), 'Simulation');
        }
      });
  }

  private httpErrorMessage(err: unknown): string {
    const e = err as { error?: unknown; message?: string; status?: number };
    if (e?.status === 0) {
      return 'Serveur injoignable (réseau, CORS ou URL paramétrage).';
    }
    const b = e?.error;
    if (typeof b === 'string' && b.trim()) {
      return b;
    }
    if (b && typeof b === 'object') {
      const o = b as { message?: string; error?: string; details?: string };
      return o.message || o.error || o.details || e?.message || 'La requête a échoué.';
    }
    return e?.message || 'La requête a échoué.';
  }
}
